// @ts-nocheck
import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Box, Checkbox, IconButton, Typography } from '@mui/material';
import { isArray, sortBy, isEmpty } from 'lodash';
import {
  ReactMuiTableV8,
  ReactMuiTableColumnHeaderTextEllipsis,
} from '@nuva-org/solytics-frontend';
import PropTypes from 'prop-types';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import CommentIcon from '@mui/icons-material/Comment';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import {
  createDynamicColumns,
  getEntityTypeKey,
  getEntityTypes,
  // getErrorHelperText,
  getLabelPathFromLabelConfig,
  getTableColumns,
  isValueEmpty,
} from '../../../../utils/Utils';
import RenderCheckListDataTypeAutocomplete from './RenderCheckListDataTypeAutocomplete';
import { DEFAULT_ROUTE_CONFIG } from '../../../../const/defaultRoutesConfig';
import {
  CHECKLIST_TYPES,
  ENTITY_TYPE,
  CHECKLIST_FIXED_COLUMN,
} from '../../../../const/CommonConst';
import ViewEntityDetailsModal from './ViewEntityDetailsModal';
import useModelInventory from '../../../../hooks/ModelInventory/useModelInventory';
import SwalToast from '../../../Common/swalToast';
import CommentsModal from './CommentsModal';

const INITIAL_PAGE_SIZE = 5;
const ITEM_HEIGHT_FOR_VIRTUALIZATION = 30;

// Context that passes checkbox column props to the header component defined outside.
// Avoids any inline component definition inside useMemo/render.
const CheckboxSelectAllContext = React.createContext(null);

const NormalCheckBox = ({ isViewMode, handleChangeCheckbox, item, value }) => {
  const handleChange = useCallback(() => {
    handleChangeCheckbox(item, value);
  }, [item, handleChangeCheckbox, value]);
  return (
    <Box
      display="flex"
      alignItems="left"
      flexDirection="column"
      width="20px"
      title={isViewMode ? 'View only' : `Select ${item}`}
    >
      <Checkbox
        checked={value}
        disabled={isViewMode}
        onClick={handleChange}
        sx={{
          padding: 0,
        }}
      />
    </Box>
  );
};

NormalCheckBox.propTypes = {
  isViewMode: PropTypes.bool.isRequired,
  handleChangeCheckbox: PropTypes.func.isRequired,
  item: PropTypes.string.isRequired,
  value: PropTypes.bool.isRequired,
};

const rowLevelOnHoverOptions = () => null;
const getHeaderProps = () => ({
  style: {
    display: 'flex',
    alignItems: 'center',
  },
});
const getRowProps = () => ({
  style: {
    position: 'relative',
  },
});
const getColumnProps = () => {
  return {
    style: {
      textAlign: 'left',
      textOverflow: 'ellipsis',
      overflow: ' hidden',
      wordBreak: 'initial',
      whiteSpace: 'nowrap',
      paddingLeft: 10,
    },
  };
};

// Per-component-instance checkbox store. Checkboxes subscribe individually
// so only the clicked cell re-renders — tableData is never mutated for checkbox operations.
function createCheckboxStore() {
  let checkedSet = new Set();
  const rowListeners = new Map();
  const headerListeners = new Set();
  return {
    isChecked: (id) => checkedSet.has(id),
    getAll: () => [...checkedSet],
    toggle: (id) => {
      if (checkedSet.has(id)) {
        checkedSet.delete(id);
      } else {
        checkedSet.add(id);
      }
      rowListeners.get(id)?.forEach((fn) => fn(checkedSet.has(id)));
      headerListeners.forEach((fn) => fn());
      return [...checkedSet];
    },
    init: (entityIds, checkedIds) => {
      checkedSet = new Set(checkedIds);
      entityIds.forEach((id) => {
        rowListeners.get(id)?.forEach((fn) => fn(checkedSet.has(id)));
      });
      headerListeners.forEach((fn) => fn());
    },
    replaceAndNotify: (newCheckedIds, entityIds) => {
      checkedSet = new Set(newCheckedIds);
      entityIds.forEach((id) => {
        rowListeners.get(id)?.forEach((fn) => fn(checkedSet.has(id)));
      });
      headerListeners.forEach((fn) => fn());
    },
    subscribeRow: (id, fn) => {
      if (!rowListeners.has(id)) rowListeners.set(id, new Set());
      rowListeners.get(id).add(fn);
      return () => rowListeners.get(id)?.delete(fn);
    },
    subscribeHeader: (fn) => {
      headerListeners.add(fn);
      return () => headerListeners.delete(fn);
    },
  };
}

// Renders a single row checkbox. Subscribes to the store for its entity_id only.
// Only this component re-renders when its row's checked state changes.
const CheckboxCell = ({ entityId, store, isViewMode, onToggle }) => {
  const [isChecked, setIsChecked] = useState(() => store.isChecked(entityId));

  useEffect(() => {
    setIsChecked(store.isChecked(entityId));
    return store.subscribeRow(entityId, setIsChecked);
  }, [entityId, store]);

  const handleClick = useCallback(() => {
    onToggle(entityId);
  }, [onToggle, entityId]);

  return (
    <Checkbox
      checked={isChecked}
      disabled={isViewMode}
      onClick={handleClick}
      sx={{ padding: 0 }}
    />
  );
};

CheckboxCell.propTypes = {
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  store: PropTypes.oneOfType([PropTypes.object]).isRequired,
  isViewMode: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

// Renders the select-all header checkbox. Subscribes to header events from the store.
// Re-renders independently of tableData and the parent component.
const CheckboxHeaderCell = ({
  store,
  tableDataRef,
  isViewMode,
  onSelectAll,
}) => {
  const [checkState, setCheckState] = useState({
    allSelected: false,
    someSelected: false,
  });

  useEffect(() => {
    const computeState = () => {
      const rows = tableDataRef.current ?? [];
      return {
        allSelected:
          rows.length > 0 && rows.every((r) => store.isChecked(r.entity_id)),
        someSelected: rows.some((r) => store.isChecked(r.entity_id)),
      };
    };
    setCheckState(computeState());
    return store.subscribeHeader(() => setCheckState(computeState()));
  }, [store, tableDataRef]);

  const { allSelected, someSelected } = checkState;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width="100%"
      marginLeft="5px"
    >
      <Checkbox
        checked={allSelected}
        className="header-checkbox"
        indeterminate={someSelected && !allSelected}
        disabled={isViewMode}
        onClick={onSelectAll}
      />
    </Box>
  );
};

CheckboxHeaderCell.propTypes = {
  store: PropTypes.oneOfType([PropTypes.object]).isRequired,
  tableDataRef: PropTypes.oneOfType([PropTypes.object]).isRequired,
  isViewMode: PropTypes.bool.isRequired,
  onSelectAll: PropTypes.func.isRequired,
};

// Defined outside the parent component so it never gets recreated during renders.
// Reads props from CheckboxSelectAllContext to avoid prop-drilling through react-table.
const CheckboxSelectAllHeader = () => {
  const ctx = useContext(CheckboxSelectAllContext);
  if (!ctx) return null;
  return (
    <CheckboxHeaderCell
      store={ctx.store}
      tableDataRef={ctx.tableDataRef}
      isViewMode={ctx.isViewMode}
      onSelectAll={ctx.onSelectAll}
    />
  );
};
const RenderCheckListAttribute = ({
  attribute,
  formikForm,
  getAllModelEntity,
  mode = 'view',
  getAllSavedSearches,
  entityId = null,
  entity_type = null,
}) => {
  const { getAllAvailableAttributes, isEntityAvailable, getAttributeDetails } =
    useModelInventory();
  const [tableData, setTableData] = useState([]);
  const [skipReset, setSkipReset] = useState(false);
  const [tableRowCount, setTableRowCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [openCommentBox, setOpenCommentBox] = useState(false);
  const [comments, setComments] = useState({});
  const [currentRowData, setCurrentRowData] = useState(null);

  const isViewMode = mode === 'view';
  const savedSearch =
    attribute?.checklist_type === CHECKLIST_TYPES?.SAVE_SEARCH;

  const attValue = useMemo(() => {
    const checklistFieldValue = formikForm?.values?.[attribute?.id];
    if (savedSearch) {
      return checklistFieldValue;
    }
    if (typeof checklistFieldValue?.checkedEntity === 'string') {
      const checkedEntity = {};
      checklistFieldValue?.checkedEntity
        ?.trim()
        ?.split(',')
        ?.forEach((option) => {
          if (!isValueEmpty(option)) {
            checkedEntity[option] = false;
          }
        });
      return {
        ...checklistFieldValue,
        checkedEntity,
      };
    }
    return checklistFieldValue;
  }, [attribute?.id, formikForm?.values, savedSearch]);

  // Ref always holds the latest attValue so stable callbacks can read it without re-creating
  const attValueRef = useRef(attValue);
  useEffect(() => {
    attValueRef.current = attValue;
  });

  // Keep formikForm and attribute in refs so debounce timer can safely read latest values
  const formikFormRef = useRef(formikForm);
  const attributeRef = useRef(attribute);
  useEffect(() => {
    formikFormRef.current = formikForm;
  });
  useEffect(() => {
    attributeRef.current = attribute;
  });

  // Checkbox event store — one instance per component mount
  const checkboxStoreRef = useRef(null);
  if (checkboxStoreRef.current === null) {
    checkboxStoreRef.current = createCheckboxStore();
  }
  // Stable ref to current page data so header/select-all can read it without deps
  const tableDataRef = useRef(tableData);
  useEffect(() => {
    tableDataRef.current = tableData;
  }, [tableData]);

  // Accumulates checkbox changes between debounced formik flushes
  const pendingCheckedRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const flushPendingToFormik = useCallback(() => {
    if (pendingCheckedRef.current === null) return;
    const checkedEntity = pendingCheckedRef.current;
    pendingCheckedRef.current = null;
    debounceTimerRef.current = null;
    formikFormRef.current?.setFieldValue(`${attributeRef.current?.id}`, {
      ...attValueRef.current,
      checkedEntity,
    });
  }, []);

  // Flush on unmount so no pending changes are lost
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      flushPendingToFormik();
    };
  }, [flushPendingToFormik]);

  // Reset pending when the filter selection changes (new data will load)
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    pendingCheckedRef.current = null;
    debounceTimerRef.current = null;
  }, [attValue?.filterName, attValue?.entityType]);

  useEffect(() => {
    if (!savedSearch || isValueEmpty(entity_type) || isValueEmpty(entityId))
      return;
    const payload = {
      entity_type: entity_type,
      entity_id: entityId,
    };
    getAttributeDetails(payload).then((res) => {
      setComments(res?.data?.[attribute?.id]?.entityComments || {});
    });
  }, [savedSearch, entity_type, entityId]);

  const [tableOperations, setTableOperations] = useState({
    showHideColumns: null,
    reOrderColumns: [],
  });
  const [availableColumnAttributes, setAvailableColumnAttributes] = useState(
    []
  );
  const [filters, setFilters] = useState([]);
  const [isOpenEntityMetaDataModal, setIsOpenEntityMetaDataModal] =
    useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  const { frontendConfig } = useSelector((state) => state?.tenant);

  const history = useNavigate();

  const handleCommentClick = (rowData) => () => {
    setCurrentRowData(rowData);
    setOpenCommentBox(true);
  };

  const handleCheckEntityExists = useCallback(
    (entityData) => {
      isEntityAvailable({
        entityType: entityData?.entity_type,
        requestType: entityData?.request_type,
        uniqueId: entityData?.unique_id,
      }).then((res) => {
        if (res?.data) {
          if (!res.data?.available) {
            SwalToast({
              icon: 'error',
              title: 'Entity is not available.',
            });
          } else {
            setSelectedEntity(entityData);
            setIsOpenEntityMetaDataModal(true);
          }
        }
      });
    },
    [setSelectedEntity, isEntityAvailable]
  );

  const getSavedSearches = useCallback(() => {
    getAllSavedSearches().then((res) => {
      setFilters(res?.data || []);
    });
  }, []);

  const updateCheckboxStatusFor = useCallback(
    (checkedEntityId) => {
      const newChecked = checkboxStoreRef.current.toggle(checkedEntityId);
      pendingCheckedRef.current = newChecked;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(flushPendingToFormik, 300);
    },
    [flushPendingToFormik]
  );

  const cellForCommentBox = useCallback(
    ({ row }) => {
      const isComment = comments?.[row?.original?.entity_id ?? ''];
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          title={isViewMode ? 'View comment' : 'Add or update comment'}
        >
          <IconButton
            aria-label="View or add comment"
            onClick={handleCommentClick(row?.original)}
          >
            <CommentIcon color={isComment ? 'primary' : 'inherit'} />
          </IconButton>
        </Box>
      );
    },
    [comments, handleCommentClick]
  );

  const cellForColsID = useCallback(
    ({ row }) => {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
          title={`Select ${row?.original?.entity_alias}`}
        >
          <CheckboxCell
            entityId={row.original.entity_id}
            store={checkboxStoreRef.current}
            isViewMode={isViewMode}
            onToggle={updateCheckboxStatusFor}
          />
        </Box>
      );
    },
    [isViewMode, updateCheckboxStatusFor]
  );

  const handleSelectAll = useCallback(() => {
    const allIds = (tableDataRef.current ?? []).map((row) => row?.entity_id);
    let prevChecked;
    if (pendingCheckedRef.current !== null) {
      prevChecked = pendingCheckedRef.current;
    } else {
      const currentAttValue = attValueRef.current;
      prevChecked = Array.isArray(currentAttValue?.checkedEntity)
        ? currentAttValue.checkedEntity
        : [];
    }
    const selectedRowsSet = new Set(prevChecked);
    const isAllRowsSelected = allIds.every((id) => selectedRowsSet.has(id));
    const newSelected = isAllRowsSelected
      ? prevChecked.filter((id) => !allIds.includes(id))
      : Array.from(new Set([...selectedRowsSet, ...allIds]));
    checkboxStoreRef.current.replaceAndNotify(newSelected, allIds);
    pendingCheckedRef.current = newSelected;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(flushPendingToFormik, 300);
  }, [flushPendingToFormik]);

  // Stable context value for CheckboxSelectAllHeader — only recreates when mode changes
  const checkboxSelectAllContextValue = useMemo(
    () => ({
      store: checkboxStoreRef.current,
      tableDataRef,
      isViewMode,
      onSelectAll: handleSelectAll,
    }),
    [isViewMode, handleSelectAll]
  );

  const handleCloseEntityMetaDataModal = useCallback(() => {
    setIsOpenEntityMetaDataModal(false);
  }, [setIsOpenEntityMetaDataModal]);

  const cellForAttributeInfo = ({ row }) => {
    const handleViewDetails = () => {
      handleCheckEntityExists(row?.original);
    };
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100%"
        title={`view details of ${row?.original?.entity_id}`}
      >
        <IconButton size="small" onClick={handleViewDetails}>
          <RemoveRedEyeIcon color="primary" />
        </IconButton>
      </Box>
    );
  };

  const getAvailableAttributes = useCallback(() => {
    getAllAvailableAttributes({ entity_type: 'ModelInventory' }).then((res) => {
      if (res?.data?.length) {
        setAvailableColumnAttributes(res.data);
      }
    });
  }, [getAllAvailableAttributes]);

  const fetchModelsRows = useCallback(
    (_pageIndex, _pageSize) => {
      const { entityType, filterName, userOfFilter } = attValue || {};
      if (_pageIndex === 0) {
        setSkipReset(false);
      } else {
        setSkipReset(true);
      }
      if (attValue) {
        setIsLoading(true);
        getAllModelEntity({
          entityType,
          filterName,
          pageIndex: _pageIndex + 1,
          pageSize: _pageSize,
          filterType: 'savedSearch',
          userOfFilter,
          entityId,
        })
          .then((res) => {
            const countRows = res.headers.get('count');
            setTableRowCount(countRows);
            setPageCount(Math.ceil(countRows / _pageSize));
            const rows = res?.data || [];
            // Update tableDataRef before store.init so CheckboxHeaderCell
            // computes allSelected against the correct page rows
            tableDataRef.current = rows;
            setTableData(rows);
            // Initialise the checkbox store for the new page
            checkboxStoreRef.current.init(
              rows.map((r) => r.entity_id),
              Array.isArray(attValueRef.current?.checkedEntity)
                ? attValueRef.current.checkedEntity
                : []
            );
          })
          .catch(() => {
            setTableData([]);
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
    [attValue?.entityType, attValue?.filterName]
  );

  const onTableStateChange = useCallback(
    ({ hiddenColumns, columnOrder }) => {
      if (hiddenColumns || isArray(hiddenColumns)) {
        setTableOperations({
          showHideColumns: hiddenColumns || [],
          reOrderColumns: columnOrder || [],
        });
      }
      if (columnOrder) {
        setTableOperations({
          showHideColumns: hiddenColumns || [],
          reOrderColumns: columnOrder || [],
        });
      }
    },
    [skipReset, setTableOperations]
  );

  const isModelAttribute = useMemo(() => savedSearch, [savedSearch]);
  const isFilterNameEmpty = useMemo(
    () => isValueEmpty(attValue?.filterName),
    [attValue?.filterName]
  );

  const dynamicColumns = useMemo(() => {
    if (availableColumnAttributes?.length > 0 && savedSearch) {
      const data = getTableColumns(availableColumnAttributes);

      const ENTITY_COLUMNS = createDynamicColumns({
        data: data,
        width: 150,
        HeaderCell: ReactMuiTableColumnHeaderTextEllipsis,
        excludeCols: ['entity_id', 'health_status'],
        disabledOrderingCols: [
          ...(frontendConfig?.afi_model_inventory_hardcoded_columns
            ? Object.keys(CHECKLIST_FIXED_COLUMN)
            : []),
        ],
      });

      const sortedColumns = sortBy(
        ENTITY_COLUMNS,
        ({ id }) =>
          CHECKLIST_FIXED_COLUMN[id] ??
          Object.keys(CHECKLIST_FIXED_COLUMN).length + 1
      );

      const detailsColumn = {
        Header: 'Details',
        accessor: 'attribute_info',
        disableSortBy: true,
        minWidth: 70,
        maxWidth: 70,
        Cell: cellForAttributeInfo,
      };

      const selectAllColumn = {
        heading: 'SELECT ALL',
        Header: CheckboxSelectAllHeader,
        accessor: 'dummy',
        disableSortBy: true,
        minWidth: 70,
        maxWidth: 70,
        Cell: cellForColsID,
        disableOrdering: true,
      };

      const commentsColumn = {
        heading: 'COMMENTS',
        Header: 'COMMENTS',
        accessor: 'no_accessor_needed',
        disableSortBy: true,
        minWidth: 90,
        maxWidth: 90,
        Cell: cellForCommentBox,
        disableOrdering: true,
      };

      const ans = [
        selectAllColumn,
        commentsColumn,
        ...(!frontendConfig?.afi_model_inventory_hardcoded_columns
          ? [detailsColumn]
          : []),
        ...sortedColumns,
        ...(frontendConfig?.afi_model_inventory_hardcoded_columns
          ? [detailsColumn]
          : []),
      ];
      return ans;
    }
    return [];
  }, [
    availableColumnAttributes,
    attribute,
    mode,
    frontendConfig?.afi_model_inventory_hardcoded_columns,
    comments,
  ]);

  const defaultHiddenColumns = useMemo(() => {
    if (!frontendConfig?.afi_model_inventory_hardcoded_columns) {
      return [];
    }
    return dynamicColumns
      .filter(
        (column) =>
          column?.id && !Object.hasOwn(CHECKLIST_FIXED_COLUMN, column.id)
      )
      .map((column) => column.id);
  }, [dynamicColumns, frontendConfig?.afi_model_inventory_hardcoded_columns]);

  useEffect(() => {
    if (isModelAttribute && isFilterNameEmpty) {
      getSavedSearches();
    }
    if (isModelAttribute && !isFilterNameEmpty) {
      fetchModelsRows(0, INITIAL_PAGE_SIZE);
    }
  }, [isModelAttribute, isFilterNameEmpty]);

  const memoizedEntityType = useMemo(() => {
    if (attValue?.entityType) {
      return { entityType: attValue?.entityType };
    }
  }, [attValue?.entityType]);

  useEffect(() => {
    if (!isEmpty(memoizedEntityType)) {
      getAvailableAttributes();
    }
  }, [memoizedEntityType]);

  const maxHeightForVirtualization = useMemo(() => {
    return ITEM_HEIGHT_FOR_VIRTUALIZATION * tableData.length >=
      document.documentElement.clientHeight * 0.45
      ? document.documentElement.clientHeight * 0.45
      : ITEM_HEIGHT_FOR_VIRTUALIZATION * tableData.length;
  }, [
    tableData,
    ITEM_HEIGHT_FOR_VIRTUALIZATION,
    document.documentElement.clientHeight,
  ]);

  const handleChangeCheckbox = useCallback(
    (item, value) => {
      formikForm?.setFieldValue(`${attribute?.id}`, {
        ...attValue,
        checkedEntity: { ...attValue?.checkedEntity, [item]: !value },
      });
    },
    [formikForm, attribute, attValue]
  );
  const handleViewModel = (row) => {
    if (row?.original?.unique_id) {
      const requestType = row?.original?.request_type;
      const entityType = row?.original?.entity_type;
      const uniqueId = row?.original?.unique_id;

      const entityTypeKey = getEntityTypeKey(entityType, requestType);

      const dynamicEntityPath = getLabelPathFromLabelConfig(
        DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.keyPath || ``,
        'path',
        DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.path
      );

      const dynamicEntityLabel = getLabelPathFromLabelConfig(
        DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.keyPath || ``,
        'label',
        DEFAULT_ROUTE_CONFIG?.[entityTypeKey]?.path
      );

      history(
        {
          pathname: `/${dynamicEntityPath}/${uniqueId}`,
        },
        {
          state: [
            {
              label: dynamicEntityLabel,
              path: `/${dynamicEntityPath}`,
            },
            {
              label: uniqueId,
              path: `/${dynamicEntityPath}/${uniqueId}`,
              type: ENTITY_TYPE[getEntityTypes(entityType, requestType)],
            },
          ],
        }
      );
    }
  };

  const handleGetCellProps = useCallback(
    (cell) => {
      return {
        onDoubleClick: () => {
          handleViewModel(cell?.row);
        },
      };
    },
    [handleViewModel]
  );

  const renderTable = () => {
    if (savedSearch && !isValueEmpty(attValue?.filterName)) {
      // Show loading only on initial load (no data yet)
      if (isLoading && tableData?.length === 0) {
        return (
          <Box
            minHeight={450}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            Loading...
          </Box>
        );
      }
      if (!isLoading && tableData?.length === 0) {
        return (
          <Box
            minHeight={450}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            No records matching search criteria
          </Box>
        );
      }
      return (
        <ReactMuiTableV8
          data={tableData?.length > 0 ? tableData : []}
          onTableStateChange={onTableStateChange}
          fetchData={fetchModelsRows}
          initialHiddenColumns={
            Array.isArray(tableOperations?.showHideColumns)
              ? tableOperations.showHideColumns
              : defaultHiddenColumns
          }
          initialColumnOrder={tableOperations?.reOrderColumns || []}
          columns={dynamicColumns}
          rowLevelOnHoverOptions={rowLevelOnHoverOptions}
          getHeaderProps={getHeaderProps}
          getRowProps={getRowProps}
          getColumnProps={getColumnProps}
          enableRowSelection={false}
          showToolbar={true}
          enableSearchBar={false}
          pageCount={pageCount}
          enablePagination={true}
          initialPageSize={INITIAL_PAGE_SIZE}
          rowsPerPageOptions={[5, 10, 15]}
          isAllOptionDisabled={true}
          skipPageReset={skipReset}
          enableVirtualization={false}
          dataLength={tableRowCount}
          enableControlledPagination={true}
          stickyHeader={true}
          stickyHeaderConfig={{
            containerStyle: {
              minHeight: 200,
            },
            tableHeadStyle: { zIndex: 1005 },
          }}
          showOptions={true}
          getCellProps={handleGetCellProps}
          enableShowHideColumnVirtualization={true}
          maxHeightForVirtualization={maxHeightForVirtualization}
          itemHeightForVirtualization={ITEM_HEIGHT_FOR_VIRTUALIZATION}
        />
      );
    }
    if (savedSearch && isValueEmpty(attValue?.filterName)) {
      if (isViewMode) {
        return '-';
      }
      return (
        <RenderCheckListDataTypeAutocomplete
          filters={filters}
          allDetailsFormikForm={formikForm}
          // section={section}
          attribute={attribute}
        />
      );
    }
  };

  const renderCheckboxList = () => {
    if (
      attribute?.checklist_type === CHECKLIST_TYPES?.NORMAL &&
      Object.keys(attValue?.checkedEntity || {})?.length !== 0
    ) {
      return (
        <Box>
          {Object.keys(attValue?.checkedEntity || {})?.map((item) => {
            return (
              <Box key={item} display="flex">
                <Box>
                  <NormalCheckBox
                    isViewMode={isViewMode}
                    item={item}
                    value={attValue?.checkedEntity?.[item]}
                    handleChangeCheckbox={handleChangeCheckbox}
                  />
                </Box>
                <Typography variant="body8">{item}</Typography>
              </Box>
            );
          })}
        </Box>
      );
    }
  };

  return (
    <CheckboxSelectAllContext.Provider value={checkboxSelectAllContextValue}>
      <Box>
        {renderTable()}
        {renderCheckboxList()}
        <ViewEntityDetailsModal
          open={isOpenEntityMetaDataModal}
          handleClose={handleCloseEntityMetaDataModal}
          selectedEntity={selectedEntity}
        />
        <CommentsModal
          isViewMode={isViewMode}
          attributeID={attribute?.id}
          entityId={entityId}
          currentRowData={currentRowData}
          comments={comments}
          setComments={setComments}
          openCommentBox={openCommentBox}
          setOpenCommentBox={setOpenCommentBox}
          entityType={entity_type}
        />
      </Box>
    </CheckboxSelectAllContext.Provider>
  );
};

export default RenderCheckListAttribute;

RenderCheckListAttribute.propTypes = {
  attribute: PropTypes.oneOfType([PropTypes.object]).isRequired,
  // section: PropTypes.oneOfType([PropTypes.object]).isRequired,
  formikForm: PropTypes.oneOfType([PropTypes.object]).isRequired,
  mode: PropTypes.oneOfType([PropTypes.string]).isRequired,
  getAllSavedSearches: PropTypes.func.isRequired,
  getAllModelEntity: PropTypes.func.isRequired,
  entityId: PropTypes.number,
  entity_type: PropTypes.string,
};
